/**
 * @file User profile management functions for Supabase
 */

import { supabase } from "@/integrations/supabase/client";
import { Profile, ProfileUpdate } from "@/__generated__/database.types";
import { DatabaseError } from "./errors";
import { handleApiError, withErrorHandling } from "@/lib/errors";
import { debug } from "@/lib/utils";

/**
 * Retrieves a user's profile by their ID
 * @param userId - The unique identifier of the user
 * @returns Promise containing the user's profile data
 */
export const getProfile = withErrorHandling(
  async (userId: string): Promise<Profile> => {
    const { data, error } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", userId)
      .single();

    if (error) {
      throw new DatabaseError("Failed to retrieve profile", { userId, error: error.message });
    }
    if (!data) {
      throw new DatabaseError("Profile not found", { userId });
    }
    return data;
  },
  null as unknown as Profile, // Fallback value - note this will cause type issues if not handled
  'Failed to load profile'
);

/**
 * Updates a user's profile information
 * @param userId - The unique identifier of the user
 * @param profile - The profile data to update
 * @returns Promise containing the updated profile data
 */
export async function updateProfile(userId: string, profile: ProfileUpdate) {
  try {
    const { data, error } = await supabase
      .from("profiles")
      .update(profile)
      .eq("id", userId);

    if (error) {
      throw new DatabaseError("Failed to update profile", { userId, profile, error: error.message });
    }
    return data;
  } catch (error) {
    // For DatabaseError, preserve the original error type
    if (error instanceof DatabaseError) {
      throw error;
    }

    // For other errors, use standard error handling
    handleApiError(error, 'Failed to update profile');
    throw error;
  }
}

/**
 * Creates a new user profile
 * @param userId - The unique identifier of the user
 * @param email - The user's email address
 * @param profile - Additional profile data
 * @returns Promise containing the created profile data
 */
export async function createProfile(userId: string, email: string, profile: ProfileUpdate) {
  try {
    const { data, error } = await supabase
      .from("profiles")
      .insert([{ id: userId, email, ...profile }]);

    if (error) {
      throw new DatabaseError("Failed to create profile", { userId, email, profile, error: error.message });
    }
    return data;
  } catch (error) {
    // For DatabaseError, preserve the original error type
    if (error instanceof DatabaseError) {
      throw error;
    }

    // For other errors, use standard error handling
    handleApiError(error, 'Failed to create profile');
    throw error;
  }
}

/**
 * Checks if a username is available (not already taken)
 * PERFORMANCE OPTIMIZATION: We convert username to lowercase to leverage the idx_profiles_username_ci
 * index created in 20250515000000_case_insensitive_usernames.sql. This provides a significant
 * performance improvement over using ILIKE for case-insensitive matching.
 *
 * @param username - The username to check
 * @returns Promise containing a boolean (true if username is available)
 */
export async function isUsernameAvailable(username: string): Promise<boolean> {
  if (!username || username.trim() === '') {
    return false;
  }

  try {
    // Use indexed LOWER function for more efficient case-insensitive query
    const { data, error } = await supabase
      .from("profiles")
      .select("id")
      .eq('username', username.toLowerCase())
      .maybeSingle();

    if (error) {
      throw new DatabaseError("Failed to check username availability", { username, error: error.message });
    }

    // Log matching usernames in development environment to help with debugging
    if (process.env.NODE_ENV === 'development' && data) {
      debug.log(`Username "${username}" matches existing username with different case.`);
    }

    // Username is available if no profile with that username exists
    return data === null;
  } catch (error) {
    // For DatabaseError, preserve the original error type
    if (error instanceof DatabaseError) {
      throw error;
    }

    // For other errors, use standard error handling
    handleApiError(error, 'Failed to check username availability');
    throw error;
  }
}

/**
 * Get profile by username
 * PERFORMANCE OPTIMIZATION: We convert username to lowercase to leverage the idx_profiles_username_ci
 * index created in 20250515000000_case_insensitive_usernames.sql. This provides a significant
 * performance improvement over using ILIKE for case-insensitive matching.
 *
 * @param username - The username to look up
 * @returns Promise containing the profile if found
 */
export async function getProfileByUsername(username: string): Promise<Profile | null> {
  if (!username || username.trim() === '') {
    return null;
  }

  try {
    // Use indexed LOWER function for more efficient case-insensitive query
    const { data, error } = await supabase
      .from("profiles")
      .select("*")
      .eq('username', username.toLowerCase())
      .maybeSingle();

    if (error) {
      throw new DatabaseError("Failed to retrieve profile by username", { username, error: error.message });
    }

    // Log in development environment if we found a profile with a different case
    if (process.env.NODE_ENV === 'development' && data && data.username !== username) {
      debug.log(`Username case difference: requested "${username}", found "${data.username}"`);
    }

    return data;
  } catch (error) {
    // For DatabaseError, preserve the original error type
    if (error instanceof DatabaseError) {
      throw error;
    }

    // For other errors, use standard error handling
    handleApiError(error, 'Failed to retrieve profile by username');
    throw error;
  }
}
